import { Component, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { of, Subscription, Observable, Subject } from 'rxjs'
import { RuleCountService, RuleCount } from 'src/app/shared/rule-count.service'
import { debounceTime, finalize, first, switchMap, takeUntil } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { ensureAwsAccount$, initializeSyncSource$ } from 'src/app/shared/resource-selector-base'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { Tag } from 'src/utils/types'
import { AccountService } from '../../services/account.service'
import { ApplicationCreatorState } from '../../services/application-creator-state.service'
import { Application, ApplicationProviderService } from '../../services/application.service'
import { SyncSource, SyncSourceProviderService } from '../../services/sync-source.service'
import { ResourceSelections } from 'src/app/shared/app-resources-selector/app-resources-selector.component'
import { ApplicationFormBase } from 'src/app/shared/application-form-base'
import { ApplicationFormBaseService } from 'src/app/shared/application-form-base.service'
import { ApplicationSaveUtility, CreateApplicationParams } from 'src/app/shared/application-save.utility'
import { ApplicationType } from '../../../services/dtos'
import { RegionsService } from 'src/app/services/regions.service'

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'application-creator-resources-page',
  templateUrl: './application-creator-resources-page.component.html',
  styleUrls: ['./application-creator-resources-page.component.scss'],
  standalone: false,
})
export class ApplicationCreatorResourcesPageComponent extends ApplicationFormBase implements OnInit {
  syncSource: SyncSource = null

  inclusionRulesCount$: Observable<RuleCount>
  exclusionRulesCount$: Observable<RuleCount>

  private syncSourceSubscription: Subscription = null
  private createApplicationSubscription: Subscription = null
  private integrationsChangeSubscription: Subscription = null
  private destroy$ = new Subject<void>()

  private rescanFlag = false

  applicationResourcesModel = this.formBuilder.group({
    applicationIntegrationIds: [new Set<string>()],
    matchingRules: [new Array<Tag>()],
    excludingRules: [new Array<Tag>()],
    resourceSelections: [{ selectedResources: [], suppressedResources: [] } as ResourceSelections],
  })

  get applicationFormModel(): UntypedFormGroup {
    return this.applicationResourcesModel
  }

  get applicationType(): ApplicationType | '' {
    return this.applicationCreatorState.applicationType
  }

  constructor(
    private accountService: AccountService,
    private applicationCreatorState: ApplicationCreatorState,
    private applicationProviderService: ApplicationProviderService,
    private syncSourceProviderService: SyncSourceProviderService,
    protected apiService: ApiService,
    private uiService: UiService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private applicationFormBaseService: ApplicationFormBaseService,
    private ruleCountService: RuleCountService,
    private regionsService: RegionsService
  ) {
    super(apiService)
  }

  protected get applicationIntegrationIds() {
    return this.applicationResourcesModel.get('applicationIntegrationIds').value
  }

  protected get accountId() {
    return this.accountService.accountId
  }

  protected get sourceEndpointUri() {
    return this.applicationCreatorState.sourceEndpointUri
  }

  protected get sourceAccountId() {
    return this.applicationCreatorState.sourceAccountId
  }

  protected get sourceRegion() {
    return this.applicationCreatorState.sourceRegion
  }

  protected get identityProviderId() {
    return this.applicationCreatorState.identityProviderId
  }

  get rpo() {
    return this.applicationCreatorState.applicationRpo
  }

  ngOnInit() {
    this.inclusionRulesCount$ = this.ruleCountService.getRuleCount$(this.applicationResourcesModel.get('matchingRules'))
    this.exclusionRulesCount$ = this.ruleCountService.getRuleCount$(
      this.applicationResourcesModel.get('excludingRules')
    )

    // Wait for account to be loaded before validating state
    // This prevents redirect on page refresh when account data is still loading
    this.accountService.accountIdStream.pipe(first(), takeUntil(this.destroy$)).subscribe((loadedAccountId) => {
      this.initializeAfterAccountLoad(loadedAccountId)
    })
  }

  private initializeAfterAccountLoad(loadedAccountId: string): void {
    // send them back to the start if we got here without account IDs and regions
    if (!this.applicationCreatorState.validAccountsAndRegions(this.regionsService)) {
      this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(loadedAccountId))
      return
    }

    // Restore matching rules from state if available
    if (this.applicationCreatorState.matchingRules.length > 0) {
      this.applicationResourcesModel.get('matchingRules').setValue(this.applicationCreatorState.matchingRules)
    }

    // Restore selected resources from state if available
    if (this.applicationCreatorState.selectedResources.size > 0) {
      const selections: ResourceSelections = {
        selectedResources: Array.from(this.applicationCreatorState.selectedResources),
        suppressedResources: [],
      }
      this.applicationResourcesModel.get('resourceSelections').setValue(selections)
    }

    // Ensure the AWS account exists, then load the Sync Source object, then launch a server-side refresh.
    this.syncSourceSubscription = of(loadedAccountId)
      .pipe(
        switchMap((_) => {
          if (this.applicationCreatorState.applicationType == ApplicationType.AWS) {
            return ensureAwsAccount$(this.apiService, this.accountId, this.applicationCreatorState.sourceAccountId)
          } else {
            return of(null)
          }
        }),
        switchMap((_awsAccountDto) => {
          return initializeSyncSource$(this.syncSourceProviderService, this.accountId, this.sourceEndpointUri)
        }),
        switchMap((syncSource) => {
          if (!syncSource.lastScanStart) {
            // source has never been refreshed.  Do that here.
            return syncSource.rescanSourceResources$(this.applicationIntegrationIds, this.identityProviderId)
          }
          return of(syncSource)
        })
      )
      .subscribe(
        (syncSource) => {
          this.syncSource = syncSource
          this._sourceResourcesLoaded = true
          this.updateResources()
        },
        (error) => {
          handleArpioError(window.location.href, this.apiService, this.uiService)
          this._sourceResourcesLoaded = true
          this.syncSourceSubscription = null
        },
        () => {
          this.syncSourceSubscription = null
          if (this.rescanFlag) {
            this.rescanFlag = false
            this.launchRescan()
          }
        }
      )

    this.initializeFormChangeSubscription()

    // Listen to changes to the set of integrations on this application
    this.integrationsChangeSubscription = this.applicationResourcesModel
      .get('applicationIntegrationIds')
      .valueChanges.pipe(debounceTime(3000))
      .subscribe((val) => {
        this.queueRescan()
      })

    // Auto-save matching rules and resource selections to state (debounced)
    this.applicationResourcesModel
      .get('matchingRules')
      .valueChanges.pipe(debounceTime(500))
      .subscribe((rules) => {
        this.applicationCreatorState.matchingRules = rules
        this.applicationCreatorState.markDirty()
      })

    this.applicationResourcesModel
      .get('resourceSelections')
      .valueChanges.pipe(debounceTime(500))
      .subscribe((selections: ResourceSelections) => {
        this.applicationCreatorState.selectedResources = new Set(selections.selectedResources)
        this.applicationCreatorState.markDirty()
      })
  }

  ngOnDestroy() {
    super.ngOnDestroy()

    this.destroy$.next()
    this.destroy$.complete()

    if (subscriptionActive(this.syncSourceSubscription)) {
      this.syncSourceSubscription.unsubscribe()
    }
    this.syncSourceSubscription = null

    if (subscriptionActive(this.createApplicationSubscription)) {
      this.createApplicationSubscription.unsubscribe()
    }
    this.createApplicationSubscription = null

    if (subscriptionActive(this.integrationsChangeSubscription)) {
      this.integrationsChangeSubscription.unsubscribe()
    }
    this.integrationsChangeSubscription = null
  }

  private launchRescan() {
    this.syncSourceSubscription = this.syncSource
      .rescanSourceResources$(this.applicationIntegrationIds, this.identityProviderId)
      .pipe
      // Do not finalize() => this.syncSourceSubscription = null here!!!
      ()
      .subscribe(
        (syncSource) => {
          this.syncSource = syncSource
          this._sourceResourcesLoaded = true
          // Rebuild the resource picker from the freshly-scanned source resources.  Without this the
          // selector keeps showing the resources captured at initial load, so newly-scanned resources
          // (e.g. a just-created ASG) never appear until a full page reload
          this.updateResources()
        },
        (error) => {
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
          this.syncSourceSubscription = null
        },
        () => {
          this.syncSourceSubscription = null
          if (this.rescanFlag) {
            this.rescanFlag = false
            this.launchRescan()
          }
        }
      )
  }

  private queueRescan() {
    if (!subscriptionActive(this.syncSourceSubscription)) {
      this.launchRescan()
    } else {
      this.rescanFlag = true
    }
  }

  refreshButtonClick() {
    /**
     * Launch a server-side refresh of the Sync Source, and wait for it to complete.
     */
    this.queueRescan()
  }

  get refreshing() {
    return subscriptionActive(this.syncSourceSubscription)
  }

  saveApplication() {
    const params: CreateApplicationParams = {
      accountId: this.accountService.accountId,
      applicationType: this.applicationCreatorState.applicationType as ApplicationType,
      applicationName: this.applicationCreatorState.applicationName,
      applicationRpo: this.applicationCreatorState.applicationRpo,
      applicationNotificationEmails: this.applicationCreatorState.applicationNotificationEmails,
      sourceEndpointUri: this.applicationCreatorState.sourceEndpointUri,
      targetEndpointUri: this.applicationCreatorState.targetEndpointUri,
      targetAccountId: this.applicationCreatorState.targetAccountId,
      matchingRules: this.matchingRules,
      excludingRules: this.excludingRules,
      resourceSelections: this.resourceSelections,
      identityProviderId: this.applicationCreatorState.identityProviderId,
    }

    this.createApplicationSubscription = ApplicationSaveUtility.createApplication$(
      params,
      this.applicationProviderService,
      this.apiService
    )
      .pipe(finalize(() => (this.createApplicationSubscription = null)))
      .subscribe((application: Application) => {
        this.uiService.announceApplication(application)
        this.applicationCreatorState.resetAndClear() // Clear localStorage on successful creation
        this.applicationFormBaseService.navigateToApplication(this.accountService.accountId, application.applicationId)
      }, this.applicationFormBaseService.handleSaveError())
  }

  get saving() {
    return this.createApplicationSubscription !== null
  }

  backButtonClick() {
    this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(this.accountId))
  }

  updateResources(): void {
    const resources = this.syncSource ? this.syncSource.resources : []
    const { selectedResources: matchingArns, suppressedResources: excludedWeakArns } = this.getResourceSelectionSets()
    this.updateResourceSelection(resources, matchingArns, excludedWeakArns, this.matchingRules, this.excludingRules)
  }

  get accountIntegrations() {
    return this.accountService.integrations
  }
}
